import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertCircle, AlertTriangle, Info, RefreshCw, Search, X, CheckCircle2 } from 'lucide-react';

interface ErrorLog {
  _id: string;
  category: 'api_error' | 'posting_error' | 'content_error' | 'system_error';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  stack?: string;
  metadata?: Record<string, unknown>;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
  updatedAt: string;
}

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const INPUT = "bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-1.5 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50 transition-all";
const BTN_SECONDARY = "inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50";

function ErrorLogsViewer() {
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLog, setSelectedLog] = useState<ErrorLog | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [resolvedFilter, setResolvedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchLogs(); }, [severityFilter, categoryFilter, resolvedFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (severityFilter !== 'all') params.append('severity', severityFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (resolvedFilter !== 'all') params.append('resolved', resolvedFilter);
      const response = await fetch(`/api/admin/errors?${params}`);
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch error logs:', err);
      setError('Failed to load error logs');
    } finally {
      setLoading(false);
    }
  };

  const handleViewLog = (log: ErrorLog) => { setSelectedLog(log); setDialogOpen(true); };

  const handleResolveLog = async (logId: string) => {
    try {
      const response = await fetch(`/api/admin/errors/${logId}/resolve`, { method: 'PUT' });
      if (response.ok) { fetchLogs(); }
      else { setError('Failed to resolve error'); }
    } catch (err) {
      console.error('Failed to resolve error:', err);
      setError('Failed to resolve error');
    }
  };

  const getSeverityIcon = (severity: string) => {
    if (severity === 'critical' || severity === 'high') return <AlertCircle size={13} className="text-red" />;
    if (severity === 'medium') return <AlertTriangle size={13} className="text-orange" />;
    return <Info size={13} className="text-primary" />;
  };

  const getSeverityCls = (severity: string) => {
    if (severity === 'critical' || severity === 'high') return 'bg-red/15 text-red';
    if (severity === 'medium') return 'bg-orange/15 text-orange';
    return 'bg-primary/15 text-primary';
  };

  const getCategoryCls = (category: string) => {
    switch (category) {
      case 'api_error': return 'bg-red/15 text-red';
      case 'posting_error': return 'bg-orange/15 text-orange';
      case 'content_error': return 'bg-primary/15 text-primary';
      case 'system_error': return 'bg-white/10 text-white/50';
      default: return 'bg-white/10 text-white/40';
    }
  };

  const filteredLogs = logs.filter(log =>
    log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (log.metadata?.platform && String(log.metadata.platform).toLowerCase().includes(searchTerm.toLowerCase())) ||
    (log.metadata?.articleId && String(log.metadata.articleId).toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const stats = {
    total: logs.length,
    critical: logs.filter(l => l.severity === 'critical').length,
    high: logs.filter(l => l.severity === 'high').length,
    resolved: logs.filter(l => l.resolved).length,
    unresolved: logs.filter(l => !l.resolved).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">Error Logs Viewer</h1>
        <button onClick={fetchLogs} disabled={loading} className={BTN_SECONDARY}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red/10 border border-red/20 text-red rounded-lg px-4 py-3 text-sm">{error}</div>}

      {/* Stats chips */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: `Total: ${stats.total}`, cls: 'bg-white/[0.06] text-white/60 border-white/[0.1]' },
          { label: `Critical: ${stats.critical}`, cls: 'bg-red/10 text-red border-red/20' },
          { label: `High: ${stats.high}`, cls: 'bg-red/10 text-red border-red/20' },
          { label: `Resolved: ${stats.resolved}`, cls: 'bg-green/10 text-green border-green/20' },
          { label: `Unresolved: ${stats.unresolved}`, cls: 'bg-orange/10 text-orange border-orange/20' },
        ].map(({ label, cls }) => (
          <span key={label} className={`px-3 py-1 rounded-full text-xs font-semibold border ${cls}`}>{label}</span>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select className={INPUT} value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select className={INPUT} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="all">All Categories</option>
          <option value="api_error">API Error</option>
          <option value="posting_error">Posting Error</option>
          <option value="content_error">Content Error</option>
          <option value="system_error">System Error</option>
        </select>
        <select className={INPUT} value={resolvedFilter} onChange={(e) => setResolvedFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="false">Unresolved</option>
          <option value="true">Resolved</option>
        </select>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text" placeholder="Search logs…" value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`${INPUT} pl-8 pr-8 w-52`}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
        </div>
      ) : (
        <div className={CARD}>
          {filteredLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-white/40 text-sm">{logs.length === 0 ? 'No error logs found' : 'No logs match your filters'}</p>
              <p className="text-white/25 text-xs mt-1">
                {logs.length === 0 ? 'Error logs will appear here when issues occur.' : 'Try adjusting your filters or search terms.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    {['Severity', 'Category', 'Message', 'Status', 'Created', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log) => (
                    <tr key={log._id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {getSeverityIcon(log.severity)}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getSeverityCls(log.severity)}`}>{log.severity}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getCategoryCls(log.category)}`}>
                          {log.category.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/60 text-xs max-w-[360px] truncate">{log.message}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${log.resolved ? 'bg-green/15 text-green' : 'bg-orange/15 text-orange'}`}>
                          {log.resolved ? 'Resolved' : 'Unresolved'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/40 text-xs whitespace-nowrap">{new Date(log.createdAt).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => handleViewLog(log)}
                            className="px-2.5 py-1 text-xs rounded-lg bg-primary/15 text-primary hover:bg-primary/25 transition-colors">
                            View
                          </button>
                          {!log.resolved && (
                            <button onClick={() => handleResolveLog(log._id)}
                              className="px-2.5 py-1 text-xs rounded-lg bg-green/15 text-green hover:bg-green/25 transition-colors flex items-center gap-1">
                              <CheckCircle2 size={11} /> Resolve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog.Root open={dialogOpen} onOpenChange={(v) => !v && setDialogOpen(false)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111113] border border-white/[0.08] rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <Dialog.Title className="text-base font-semibold text-white">
                Error Details — {selectedLog?.category.replace('_', ' ')}
              </Dialog.Title>
              <Dialog.Close className="text-white/40 hover:text-white transition-colors"><X size={18} /></Dialog.Close>
            </div>

            {selectedLog && (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-primary/80 mb-1 uppercase tracking-wider">Message</p>
                  <p className="text-white/80 text-sm">{selectedLog.message}</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getSeverityCls(selectedLog.severity)}`}>
                    Severity: {selectedLog.severity}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getCategoryCls(selectedLog.category)}`}>
                    {selectedLog.category.replace('_', ' ')}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${selectedLog.resolved ? 'bg-green/15 text-green' : 'bg-orange/15 text-orange'}`}>
                    {selectedLog.resolved ? 'Resolved' : 'Unresolved'}
                  </span>
                </div>

                <div className="text-white/40 text-xs space-y-0.5">
                  <p>Created: {new Date(selectedLog.createdAt).toLocaleString()}</p>
                  {selectedLog.resolvedAt && <p>Resolved: {new Date(selectedLog.resolvedAt).toLocaleString()}</p>}
                </div>

                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-primary/80 mb-2 uppercase tracking-wider">Metadata</p>
                    <div className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-3 font-mono text-xs text-white/50 space-y-0.5">
                      {Object.entries(selectedLog.metadata).map(([key, value]) => (
                        <div key={key}>
                          <span className="text-white/70">{key}:</span>{' '}
                          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedLog.stack && (
                  <div>
                    <p className="text-xs font-semibold text-primary/80 mb-2 uppercase tracking-wider">Stack Trace</p>
                    <pre className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-3 font-mono text-[10px] text-white/40 overflow-auto max-h-64 whitespace-pre-wrap">
                      {selectedLog.stack}
                    </pre>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-white/[0.06]">
              <button onClick={() => setDialogOpen(false)}
                className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors">
                Close
              </button>
              {selectedLog && !selectedLog.resolved && (
                <button
                  onClick={() => { handleResolveLog(selectedLog._id); setDialogOpen(false); }}
                  className="inline-flex items-center gap-1.5 bg-green/15 hover:bg-green/25 text-green border border-green/20 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors">
                  <CheckCircle2 size={14} /> Mark as Resolved
                </button>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

export default ErrorLogsViewer;
